export const FLEX_PACK_VOUCHER_SERVICE_FEE = 1;
export function parseFlexPackPrice(flexPack) {
    const raw = flexPack?.price;
    if (raw === null || raw === undefined || raw === '')
        return 0;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
}
/** Sponsorship/comp flex packs have package price $0 and waive per-voucher fees. */
export function isCompFlexPack(flexPack) {
    return parseFlexPackPrice(flexPack) === 0;
}
export function getFlexPackVoucherCount(flexPack) {
    return Math.max(0, Math.floor(Number(flexPack?.gameTickets) || 0));
}
/** Blocktickets service fee: $1 per included voucher unless comp/sponsorship. */
export function getFlexPackVoucherServiceFee(flexPack) {
    if (isCompFlexPack(flexPack))
        return 0;
    return getFlexPackVoucherCount(flexPack) * FLEX_PACK_VOUCHER_SERVICE_FEE;
}
export function getFlexPackCartSubtotal(flexPack) {
    return parseFlexPackPrice(flexPack) + getFlexPackVoucherServiceFee(flexPack);
}
export function isZeroFeeCompFlexPackCart(flexPack) {
    return getFlexPackCartSubtotal(flexPack) === 0;
}
function roundMoney(value) {
    const n = Number(value);
    if (!Number.isFinite(n))
        return 0;
    return Math.round(n * 100) / 100;
}
/** Matches flex-pack checkout, which rounds card math with toFixed. */
function roundTo2(value) {
    const n = Number(value || 0);
    if (!Number.isFinite(n))
        return 0;
    return Number(n.toFixed(2));
}
function normalizeRate(value) {
    const numeric = Number(value || 0);
    if (!Number.isFinite(numeric))
        return 0;
    return numeric > 1 ? numeric / 100 : numeric;
}
function recordAttributes(value) {
    if (!value || typeof value !== 'object')
        return null;
    const record = value;
    const attributes = record.attributes;
    if (attributes && typeof attributes === 'object') {
        return { ...record, ...attributes };
    }
    return record;
}
function readCustomFees(flexPack) {
    const rawSource = flexPack?.customFees ?? flexPack?.custom_fees;
    const raw = Array.isArray(rawSource) ? rawSource : [];
    return raw
        .map((fee) => {
        const attrs = recordAttributes(fee);
        if (!attrs)
            return null;
        const id = Number(attrs.id);
        const feeAmount = Number(attrs.feeAmount ?? attrs.fee_amount);
        if (!Number.isFinite(id) || id <= 0)
            return null;
        if (!Number.isFinite(feeAmount) || feeAmount <= 0)
            return null;
        const name = String(attrs.name || 'Custom fee').trim() || 'Custom fee';
        return {
            id,
            name,
            option: String(attrs.option || 'amount').toLowerCase(),
            feeAmount,
        };
    })
        .filter((fee) => Boolean(fee));
}
function customFeeLineAmount(fee, percentBase) {
    if (fee.option === 'percent') {
        return roundMoney((percentBase * fee.feeAmount) / 100);
    }
    return roundMoney(fee.feeAmount);
}
/**
 * Fan charge for a flex pack. Same numbers checkout stores on the cart:
 * pack price, $1 per voucher, sales tax on that subtotal, then card
 * processing. Custom fees are added once and processing is recalculated
 * on the new charge. `total` stays the cart subtotal (pack + voucher fees
 * + custom fees). `buyerTotal` is what the fan pays.
 */
export function quoteFlexPackPurchase(flexPack) {
    const comp = isCompFlexPack(flexPack);
    const basePrice = parseFlexPackPrice(flexPack);
    const voucherFee = getFlexPackVoucherServiceFee(flexPack);
    const subtotal = getFlexPackCartSubtotal(flexPack);
    const organization = recordAttributes(flexPack?.organization);
    const taxRateRecord = recordAttributes(organization?.taxRate ?? organization?.tax_rate);
    const feeStructure = recordAttributes(organization?.fee_structure ?? organization?.feeStructure);
    const pricedWithTaxesAndFees = Boolean(taxRateRecord && feeStructure);
    const taxRate = Number(taxRateRecord?.estimatedCombinedRate);
    const salesTax = organization?.isTaxable && !comp && Number.isFinite(taxRate)
        ? roundTo2(subtotal * taxRate)
        : 0;
    const stripeCharge = Number(feeStructure?.stripeCharge || 0);
    const stripeRate = normalizeRate(feeStructure?.stripeServicePecentage);
    const processingFor = (amount) => {
        if (comp)
            return 0;
        const charge = Number(amount) || 0;
        if (charge <= 0)
            return 0;
        return roundTo2(stripeCharge + charge * stripeRate);
    };
    const processingBeforeCustomFees = processingFor(subtotal + salesTax);
    const chargedBeforeCustom = roundMoney(subtotal + salesTax + processingBeforeCustomFees);
    const fees = comp || chargedBeforeCustom <= 0 ? [] : readCustomFees(flexPack);
    const customFeeLines = fees
        .map((fee) => {
        const amount = customFeeLineAmount(fee, chargedBeforeCustom);
        if (amount <= 0)
            return null;
        return { id: fee.id, name: fee.name, amount };
    })
        .filter((line) => Boolean(line));
    const customFeeAmount = roundMoney(customFeeLines.reduce((sum, line) => sum + line.amount, 0));
    let processingFee = processingBeforeCustomFees;
    let packageWebsiteFeeSnapshot = null;
    if (customFeeAmount > 0) {
        const baseWithoutProcessing = roundMoney(chargedBeforeCustom - processingBeforeCustomFees);
        const chargeBase = roundMoney(baseWithoutProcessing + customFeeAmount);
        processingFee = processingFor(chargeBase);
        const totalPrice = roundMoney(baseWithoutProcessing + customFeeAmount + processingFee);
        packageWebsiteFeeSnapshot = {
            source: 'package-custom-fee',
            processingFee,
            totalPrice,
            customFeeId: customFeeLines[0].id,
            customFeeName: customFeeLines[0].name,
            customFeeAmount,
            customFeeLines,
            customFeePercentBase: chargedBeforeCustom,
            customFeeIds: customFeeLines.map((line) => line.id),
        };
    }
    const total = roundMoney(subtotal + customFeeAmount);
    const buyerTotal = roundMoney(total + salesTax + processingFee);
    return {
        basePrice,
        voucherFee,
        subtotal,
        salesTax,
        processingFee,
        customFeeAmount,
        total,
        totalTax: salesTax,
        buyerTotal,
        pricedWithTaxesAndFees,
        packageWebsiteFeeSnapshot,
    };
}
